// 58 §4 — THE EVENT-TYPE FILTER, corrected and then shared. In that order,
// because sharing a wrong mechanism to three more surfaces is how a defect
// becomes four.
//
// WHAT WAS WRONG, and it was wrong in Agenda too — the only place the filter
// existed. `AgendaView.tsx` built its chip set from the EVENTS THAT EXIST:
//
//     const allTypes = [...new Set(ag.map((a) => a.type || "OTHER"))].sort();
//
// Three consequences, none of them announced:
//   * a type added in Settings got NO CHIP until somebody created an event of
//     that type — so the taxonomy could not be exercised from the screen that
//     filters by it;
//   * the chips sorted ALPHABETICALLY BY CODE, never in the order the firm put
//     the taxonomy in;
//   * the colour came from a lookup that could miss, so a code with no entry
//     fell back to grey without saying why.
//
// THE CORRECTION: the taxonomy is the source. `evtTypes` (the folded
// `event_type` display list — `{ code, desc, color }`, already handed to all
// four surfaces) supplies the chips, in its own order, with its own colours.
//
// AND AN EVENT WHOSE TYPE THE TAXONOMY NO LONGER HOLDS DOES NOT VANISH. That
// state exists today — the old chip set proved it, because it could only be
// built from codes real events carry. Taxonomy-sourced chips alone would drop
// such a code from the strip, and `hiddenTypes` only hides what is explicitly
// switched off, so the EVENT would still render while being unfilterable and
// unnamed. Instead every orphan code carried by an event in scope is APPENDED
// after the taxonomy's own, flagged `orphan`, so it keeps a chip, keeps its
// toggle, and is visibly not one of the firm's types. Nothing disappears
// quietly; the reader is told.
//
// UNTYPED is the same "OTHER" bucket the agenda has always used for an event
// with no type at all. It is not a taxonomy code and never was.

import type { EventTypeDisplay } from "./taxonomyTypes";
import { isDividerCode } from "../utils/taxonomyMap";

export const UNTYPED_EVENT_CODE = "OTHER";

export type EventTypeChip = {
  code: string;
  label: string;
  color: string;
  /** the code is on an event but not in the taxonomy (or is the untyped bucket) */
  orphan: boolean;
};

/** The filter's stored shape: { code: boolean }, or null meaning "everything on". */
export type EventTypeFilter = Record<string, boolean> | null | undefined;

type EventRow = { type?: string | null };

const GREY = "#8892a4";

/**
 * The chip set: the taxonomy's own entries in the taxonomy's own order, then
 * any code an event in scope carries that the taxonomy does not hold.
 */
export function eventTypeChips(
  evtTypes: readonly EventTypeDisplay[] | null | undefined,
  events: readonly EventRow[] | null | undefined,
): EventTypeChip[] {
  const out: EventTypeChip[] = [];
  const known = new Set<string>();
  for (const e of evtTypes || []) {
    if (!e || !e.code || known.has(e.code)) continue;
    /* BRIEF 108 §6b — a divider is layout, not a type: no chip, and never an
       orphan (it is skipped before `known`, and no event can carry its code —
       the reserved prefix is refused for ordinary entries server-side). */
    if (isDividerCode(e.code)) continue;
    known.add(e.code);
    out.push({ code: e.code, label: e.code, color: e.color || GREY, orphan: false });
  }
  const strays = new Set<string>();
  for (const ev of events || []) {
    const code = (ev && ev.type) || UNTYPED_EVENT_CODE;
    if (!known.has(code)) strays.add(code);
  }
  for (const code of [...strays].sort()) {
    out.push({ code, label: code, color: GREY, orphan: true });
  }
  return out;
}

/** Is this code showing? An absent entry means on — the filter stores exclusions. */
export function isEventTypeOn(filt: EventTypeFilter, code: string): boolean {
  return filt ? (filt[code] ?? true) : true;
}

/** Does this event survive the filter? One predicate, so four surfaces agree. */
export function eventTypeVisible(filt: EventTypeFilter, ev: EventRow): boolean {
  if (!filt) return true;
  return isEventTypeOn(filt, (ev && ev.type) || UNTYPED_EVENT_CODE);
}

/**
 * Clicking a chip. Verbatim from AgendaView's `toggleType`, with `allTypes`
 * replaced by the chip set:
 *   multi  — flip this one, leave the rest
 *   single — isolate this one; clicking the already-isolated one clears
 */
export function toggleEventType(
  filt: EventTypeFilter,
  code: string,
  chips: readonly EventTypeChip[],
  multi: boolean,
): Record<string, boolean> | null {
  const codes = chips.map((c) => c.code);
  if (multi) {
    const cur = filt || Object.fromEntries(codes.map((x) => [x, true]));
    return { ...cur, [code]: !(cur[code] ?? true) };
  }
  const cur = filt || null;
  const isExclusive =
    !!cur && codes.filter((x) => cur[x] ?? true).length === 1 && (cur[code] ?? true);
  if (isExclusive) return null;
  return Object.fromEntries(codes.map((x) => [x, x === code]));
}

/** `rgba(r,g,b,a)` from a `#rrggbb`, falling back to the grey the strip uses. */
export function chipTint(color: string | null | undefined, alpha: number): string {
  const hex = /^#[0-9a-fA-F]{6}$/.test(color || "") ? (color as string) : GREY;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
